/**
 * Local placeholder strings for Spot AI.
 *
 * Kept out of `t()` so that `generate:translations` (which scans `t('...')`
 * in --strict mode) does not fail. Usage: `tLocal('local_footer_copyright')`.
 *
 * ── Categories ─────────────────────────────────────────────────────────────
 *
 * ✅ INTENTIONALLY LOCAL — never migrate to Bocal:
 *   • Brand names (Deezer, Spotify, Apple Music, SoundCloud, YouTube Music)
 *   • Footer chrome that contains the brand or the © year
 *   • Dev-only `/bypass` page (not user-facing)
 *
 * 🕒 AWAITING BOCAL KEY CREATION (no Bocal key yet — ping copy team):
 *   • local_footer_language_label
 *   • local_hero_cta_error_{title,description} (FR copy in inline comment)
 *   • local_labeling_image_alt
 *   • local_result_band_label_* (5 wording tiers shared across 11 bands —
 *     used as a11y aria-labels via `bandPresentation.bandLabelKey()`)
 *   • local_result_{tracks_count,empty_title,share_title,share_error_*}
 *
 * 📜 LEGAL PAGES — final FR/EN copy is hardcoded in `src/modules/legal/content.ts`
 *   per CROSS-970/CROSS-971, so it intentionally stays outside Bocal for now.
 */

const LOCAL_STRINGS = {
	local_bypass_title: 'Bypass gatekeeper (dev)',
	local_entry_back_to_landing: 'Back to the AI music detector overview',
	local_entry_error:
		'We could not start your library analysis. Please try again from the overview page.',
	local_entry_retry: 'Try again',
	local_entry_subtitle:
		'We are preparing your secure library import before sending you to TuneMyMusic.',
	local_entry_title: 'Preparing your analysis',
	local_footer_branding: 'Deezer',
	local_footer_copyright: '© 2026 Deezer',
	local_footer_language_label: 'Language',
	local_hero_platform_apple_music: 'Apple Music',
	local_hero_platform_soundcloud: 'SoundCloud',
	local_hero_platform_spotify: 'Spotify',
	local_hero_platform_youtube_music: 'YouTube Music',
	local_labeling_image_alt:
		'Screenshot of an AI-labeled track in the Deezer app',
	local_result_band_label_critical: 'Critical',
	local_result_band_label_insufficient: 'Not enough data',
	local_result_band_label_light: 'Light',
	local_result_band_label_minimal: 'Minimal',
	local_result_band_label_moderate: 'Moderate',
	local_result_band_label_significant: 'Significant',
	local_result_tracks_count: '%(count)s tracks',
	local_result_empty_title: 'No results yet',
	local_result_share_error_description:
		'Please try again. If the problem persists, download your score card later.',
	local_result_share_error_title: 'Could not prepare your share card',
	local_result_share_title: 'My Spot AI score',
	local_seo_analyze_description:
		'Spot AI is scanning your imported playlists to detect AI-generated music.',
	local_seo_analyze_title: 'Scanning your playlists | Deezer',
	local_seo_bypass_description:
		'Development-only Spot AI bypass page for Deezer QA.',
	local_seo_bypass_title: 'Spot AI dev bypass | Deezer',
	local_seo_error_reach_limits_description:
		'You have reached the current Spot AI analysis limit. Come back later to scan more playlists.',
	local_seo_error_unknown_description:
		'Something went wrong while loading Spot AI. Refresh the page or try again later.',
	local_seo_import_description:
		'Spot AI is importing your playlists before scanning them for AI-generated tracks.',
	local_seo_import_title: 'Importing your music library | Deezer',
	local_seo_result_description:
		'See your Spot AI score and how much AI-generated music appears in your playlists.',
	local_seo_result_title: 'AI playlist results | Deezer',
	// EN strings — when shipping for real, replace these tLocal() calls with
	// proper Bocal keys (`spotai_*_standalone-web`) and pull translations via
	// `npm run generate:translations`. FR copy for the translator below:
	//   title: 'Impossible de lancer l’analyse'
	//   description: 'Vérifie ta connexion et réessaie. Si le problème persiste, reviens dans quelques minutes.'
	local_hero_cta_error_title: 'Could not start the analysis',
	local_hero_cta_error_description:
		'Check your connection and try again. If the issue persists, please come back in a few minutes.',
} as const;

export type LocalKey = keyof typeof LOCAL_STRINGS;

export const tLocal = (key: LocalKey): string => LOCAL_STRINGS[key];
